import { observer } from 'mobx-react';
import { FC } from 'react';
import Button from '~/components/Button/Button';
import { Candidate } from '~/definitions/candidate';
import { Vote } from '~/definitions/vote';
import { getStore } from '~/mobx/AppStore';
import BackLink from '~public/img/icons/arrow-left-secondary-light.svg';

interface Props {
    candidates: Candidate[];
    isDisabledVote: boolean;
    onClose: (isClosed: boolean) => void;
    onSubmit: () => void;
    votes: Vote[];
    getVoteType: (candidateId: string) => string;
    isOpen?: boolean;
    regionName?: string;
}

const VoteSummaryModal: FC<Props> = (props: Props) => {
    const { candidates, isDisabledVote, onClose, onSubmit, isOpen, regionName } = props;

    const liClassName =
        'border-t border-t-[#c2ccce] py-[10px] px-[20px] flex justify-between items-center text-[14px] md:text-[16px]';
    const cancelVoteClassName = 'text-custom-red-2 line-through';
    const toggleStyles = isOpen ? 'max-h-[756px]' : 'max-h-0';
    const modalContainerClassName = `vote-summary fixed bottom-0 left-0 right-[15px] w-full border border-[rgba(0,0,0,0.2)] bg-[#fff] transition-all duration-300 ease-in-out md:absolute md:left-auto md:right-0 md:max-w-[400px] ${toggleStyles}`;
    const buttonBarClassName =
        'button-bar flex items-center justify-between bg-cover bg-center bg-no-repeat px-[10px] py-[15px] md:py-[25px]';
    const backButtonClassName = 'btn inline-flex items-center text-[0.9em]';
    const submitButtonClassName =
        '!border-custom-green-1 !bg-custom-green-1 hover:!border-custom-green-2 hover:!bg-custom-green-2';

    const renderVoteLine = (candidates: Candidate[]) => {
        const lines = candidates.map((person) => {
            const isExtraVoted = props.getVoteType(person.id) === 'extra';
            const isDownVoted = props.getVoteType(person.id) === 'down';

            return (
                <li key={person.id} className={`${liClassName} ${isDownVoted ? (cancelVoteClassName ?? '') : ''}`}>
                    {person.firstName} {person.lastName}
                    {isExtraVoted && <span className="extra ml-2 text-[13px]">+ ekstrastemme</span>}
                </li>
            );
        });

        return <ul className="candidate-line-list max-h-[570px] overflow-y-auto">{lines}</ul>;
    };

    return (
        <div className={modalContainerClassName} id="vote-summary">
            <div className="content pt-[20px] md:pt-[30px]">
                <div className="pb-[10px] text-center">
                    <h2 className="m-0 text-[1.2em]">Min stemmeseddel</h2>
                    <div className="text-[0.9em]">{regionName}</div>
                </div>
                {candidates.length > 0 && renderVoteLine(candidates)}
                <div className={buttonBarClassName} style={{ backgroundImage: "url('/img/bg/bg-summary-vote.png')" }}>
                    <Button
                        icon={<BackLink />}
                        btnText="Tilbake"
                        testId="back-link"
                        variant="link"
                        extendedClassName={backButtonClassName}
                        onClick={() => onClose(false)}
                    />
                    <Button
                        btnText={'Avgi stemme'}
                        testId={'btn-vote'}
                        disabled={isDisabledVote}
                        onClick={onSubmit}
                        extendedClassName={submitButtonClassName}
                    />
                </div>
            </div>
        </div>
    );
};

const VoteSummaryModalWithStore = () => {
    const { voteStore, candidateStore, ballotStore, previewStore } = getStore();
    const isPreview = previewStore.isInitialized;
    const isDisabledVote = candidateStore.isDisabledVote || isPreview;
    const candidates = candidateStore.candidates;
    const isOpen = candidateStore.isOpenVoteSummaryModal;
    const regionName = ballotStore.region?.regionName;

    return (
        <VoteSummaryModal
            candidates={candidates}
            votes={voteStore.votes}
            isDisabledVote={isDisabledVote}
            onClose={(isClosed: boolean) => candidateStore.setIsOpenVoteSummaryModal(isClosed)}
            onSubmit={voteStore.submitVote}
            getVoteType={voteStore.getVoteType}
            isOpen={isOpen}
            regionName={regionName}
        />
    );
};

export default observer(VoteSummaryModalWithStore);
